import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";

const ACCENT = "#f8f2f2";

const PHASES = [
  {
    id: "pre",
    title: "Pre-Production",
    blurb:
      "Visualize the project, build the pitch, and establish your presence before cameras roll.",
    services: [
      {
        id: "ai",
        title: "AI Asset Generation",
        tagline: "Polished visuals, fast, directed by people who know production.",
        description:
          "Concept art, key art, and motion content created with leading generative image and video models, directed by people who understand production.",
        involves: [
          "Concept art, storyboards, and look development",
          "Posters, key art, and social campaign visuals",
          "Short AI-generated video clips and animated loops",
          "Custom workflows built around Higgsfield and other leading models",
        ],
        fit: "Productions that need polished visuals fast, without a full art department.",
      },
      {
        id: "web",
        title: "Websites & Online Stores",
        tagline: "A professional home on the web for small productions.",
        description:
          "A professional online presence for small productions, from a simple marketing site to a full store for merch and digital products.",
        involves: [
          "Portfolio, trailer, and press kit pages",
          "Film launch and crowdfunding landing pages",
          "Online store setup for merch, prints, and downloads",
          "Mobile-friendly design, contact forms, and domain setup",
        ],
        fit: "Independent filmmakers and small production companies launching or refreshing their online presence.",
      },
    ],
  },
  {
    id: "prod",
    title: "Production",
    blurb:
      "Keep your team, schedule, and footage organized while you're in the thick of it.",
    services: [
      {
        id: "workflows",
        title: "Workflow Tools",
        tagline: "Move projects from kickoff to delivery without the spreadsheets.",
        description:
          "Custom approvals, status tracking, and handoffs that fit how your studio really operates, so nothing falls through the cracks between departments.",
        involves: [
          "Project stages, assignments, and deadlines",
          "Automated notifications and reminders",
          "Approval chains for internal and client sign-off",
          "Dashboards that show where everything stands",
        ],
        fit: "Teams running projects on spreadsheets, sticky notes, and group chats.",
      },
      {
        id: "assets",
        title: "Asset Management",
        tagline: "Find any file in seconds, not hours.",
        description:
          "A searchable, organized library for footage, graphics, audio, documents, and every version in between, structured around the way your team already works.",
        involves: [
          "Tagging, metadata, and fast search",
          "Project and collection organization",
          "Version tracking and a clear delivery record",
          "Permissions for staff, freelancers, and clients",
        ],
        fit: "Studios whose libraries have outgrown shared drives and folder naming rules.",
      },
    ],
  },
  {
    id: "post",
    title: "Post-Production",
    blurb:
      "Review, approve, host, and deliver the finished work, then get it in front of an audience.",
    services: [
      {
        id: "portals",
        title: "Client Portals",
        tagline: "One branded place for every review, note, and approval.",
        description:
          "Give clients a polished, private space to watch cuts, leave timecoded feedback, and sign off on deliverables, with no more hunting through email threads or expired transfer links.",
        involves: [
          "Branded login and project dashboards for each client",
          "Frame-accurate commenting and approval tracking",
          "Version history so everyone knows which cut is current",
          "Secure final delivery and download",
        ],
        fit: "Studios tired of feedback scattered across email, text, and chat.",
      },
      {
        id: "hosting",
        title: "Media Hosting",
        tagline: "Fast, reliable playback for the files that matter.",
        description:
          "Stream and deliver large video, audio, and image files smoothly to viewers anywhere, with control over exactly who can see what.",
        involves: [
          "Responsive streaming for dailies, screeners, and finals",
          "Access control by client, project, or individual link",
          "Support for large, high-resolution source files",
          "Your branding, your player, your domain",
        ],
        fit: "Teams sharing screeners and deliverables who want more control than a generic file-sharing link.",
      },
      {
        id: "streaming",
        title: "Streaming & Distribution",
        tagline: "Your films, your platform, your audience.",
        description:
          "A branded home for your films, series, and shorts, so audiences watch on your site, on your terms.",
        involves: [
          "Branded catalog with trailers, thumbnails, and descriptions",
          "Smooth playback on desktop, tablet, and mobile",
          "Public, members-only, or restricted access",
          "Your domain, your design, your audience",
        ],
        fit: "Filmmakers and studios who want to own their audience instead of renting space on a third-party platform.",
      },
    ],
  },
];

const STEPS = [
  {
    title: "Discover",
    text: "We learn your workflow, your team, and where projects get stuck.",
  },
  {
    title: "Design",
    text: "We map the experience for your staff and your clients before any code is written.",
  },
  {
    title: "Build",
    text: "We develop in clear stages, so you see working software early and often.",
  },
  {
    title: "Launch & Support",
    text: "We deploy, train your team, and stay on to maintain and improve the platform.",
  },
];


const styles = `
.ss-page {
  margin-bottom: 7%;
  color: rgb(248, 242, 242);
}
.ss-fade { animation: ssFade 0.6s ease both; }
@keyframes ssFade {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Shared card look, matching .card-body */
.ss-card {
  border: 2px solid rgba(28, 26, 26, 0.9);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.03) inset,
    0 -2px 0 rgba(0, 0, 0, 0.5) inset,
    0 12px 24px rgba(0, 0, 0, 0.6),
    0 32px 64px rgba(0, 0, 0, 0.5),
    0 64px 100px rgba(0, 0, 0, 0.35);
  background: linear-gradient(
    324deg,
    rgba(116, 12, 12, 0.07),
    rgba(92, 20, 20, 0.6),
    rgba(105, 13, 13, 0.6),
    rgba(58, 4, 4, 0.6)
  );
  background-size: 300% 300%;
  animation: gradientAnimation 10s ease infinite;
}

.ss-hero { text-align: center; padding: 0rem 1rem 0rem; }
.ss-eyebrow {
  color: ${ACCENT};
  -webkit-text-fill-color: ${ACCENT};
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin-bottom: 0.75rem;
}
.ss-hero h1 { font-size: clamp(2.2rem, 5vw, 3.6rem); margin-bottom: 1rem; }
.ss-hero p {
  max-width: 680px;
  margin: 0 auto;
  font-size: clamp(16px, 1.8vmax, 18px);
  
}
.ss-rule {
  width: 70px; height: 3px; background: ${ACCENT};
  margin: 1.5rem auto 0; border-radius: 2px;
}

.ss-section-title {
  text-align: center;
  margin: 3rem 0 1.5rem;
  position: relative;
  z-index: 2;
}
.ss-phase { margin-top: 3rem; }
.ss-phase-header {
  text-align: center;
  padding: 2rem 2rem 0;
}
.ss-phase-title { margin: 0 0 0.5rem; }
.ss-phase-blurb {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(248, 242, 242, 0.35);
}
.ss-panel-wrap {
  border-radius: 30px;
  overflow: hidden;
}

.ss-tabs {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.ss-tabs::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.65) 0%,
    rgba(0, 0, 0, 0.35) 55%,
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 70px);
  mask-image: linear-gradient(180deg, transparent 0, #000 70px);
}
.ss-tab {
 position: relative;
  display: flex; align-items: center; gap: 1rem;
  text-align: left; width: 100%;
  background: transparent; color: rgb(248, 242, 242);
  border: 0; border-left: 3px solid transparent;
  padding: 1.1rem 1.25rem;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  opacity: 0.65;
  font-size: 1.1rem;
}
.ss-tab:hover {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0));
  opacity: 1;
}
.ss-tab:focus-visible { outline: 2px solid ${ACCENT}; outline-offset: -2px; }
.ss-tab.active {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0));
  border-left-color: ${ACCENT};
  opacity: 1;
}
.ss-tab-num {
  color: ${ACCENT};
  -webkit-text-fill-color: ${ACCENT};
  font-weight: 700; font-size: 0.9rem;
}

.ss-content { padding: 2rem 2.25rem; min-height: 100%; }
.ss-content h3 { margin-bottom: 0.25rem; }
.ss-content p { font-size: clamp(16px, 1.8vmax, 18px); }
.ss-tagline {
  color: ${ACCENT};
  -webkit-text-fill-color: ${ACCENT};
  font-style: italic; margin-bottom: 1rem;
}
.ss-involves { list-style: none; padding: 0; margin: 1.25rem 0; }
.ss-involves li { padding: 0.45rem 0 0.45rem 1.6rem; position: relative; font-size: clamp(16px, 1.8vmax, 18px); }
.ss-involves li::before {
  content: ""; position: absolute; left: 0; top: 0.95rem;
  width: 8px; height: 8px; border-radius: 50%; background: ${ACCENT};
}
.ss-fit {
  border-top: 1px solid rgba(248, 242, 242, 0.2);
  padding-top: 1rem; margin-top: 1.25rem; 
}
.ss-fit strong { color: ${ACCENT}; -webkit-text-fill-color: ${ACCENT}; }

@media (max-width: 767px) {
   .ss-tabs {
    flex-direction: row;
    overflow-x: auto;
  }
  .ss-tabs::before {
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.45) 50%,
      rgba(0, 0, 0, 0)
    );
    -webkit-mask-image: none;
    mask-image: none;
  }
  .ss-tab {
    border-left: 0; border-bottom: 3px solid transparent;
    white-space: nowrap; width: auto; flex: 0 0 auto;
  }
  .ss-tab.active { border-bottom-color: ${ACCENT}; }
  .ss-content { padding: 1.5rem; }
}

.ss-step {
  height: 100%;
  padding: 1.5rem;
  border-radius: 24px;
  transition: transform 0.25s ease, border-color 0.25s ease;
}
.ss-step:hover { transform: translateY(-4px); border-color: ${ACCENT}; }
.ss-step-num {
  color: ${ACCENT};
  -webkit-text-fill-color: ${ACCENT};
  font-size: 2rem; font-weight: 700; line-height: 1;
}
.ss-step h5 { margin: 0.75rem 0 0.5rem; }

.ss-step .ss-step-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}
.ss-step .ss-step-header h5 {
  margin: 0;
}
.ss-step p { margin: 0;}

.ss-cta {
  margin-top: 4rem; padding: 3rem 1.5rem; text-align: center;
  border-radius: 30px;
}
.ss-cta p { max-width: 560px; margin: 0.75rem auto 1.5rem;}
.ss-btn {
  display: inline-block; padding: 0.8rem 2rem; border-radius: 999px;
  background: ${ACCENT}; color: #2a0707; -webkit-text-fill-color: #2a0707;
  font-weight: 700; text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
}
.ss-btn:hover {
  color: #2a0707; transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(232, 165, 152, 0.35);
}
.ss-page p {
  font-size: clamp(16px, 1.8vmax, 18px) !important;
}

.ss-page :is(p, li, strong, button, span, h13, .ss-fit, .ss-tab-num, .ss-step-num, .ss-eyebrow) {
  -webkit-text-fill-color: rgb(248, 242, 242) !important;
}

.ss-page .ss-btn {
  -webkit-text-fill-color: #2a0707 !important;
  font-size: clamp(16px, 1.8vmax, 18px) !important;
}
`;

const CONTACT_HREF =
  "mailto:contact@secondteamproductions.com?subject=Studio%20Services%20Inquiry";

function PhaseCard({ phase }) {
  const [active, setActive] = useState(phase.services[0].id);
  const tabRefs = useRef([]);
  const services = phase.services;
  const current = services.find((s) => s.id === active);

  const handleKeyDown = (e, index) => {
    const keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const dir = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1;
    const next = (index + dir + services.length) % services.length;
    setActive(services[next].id);
    tabRefs.current[next]?.focus();
  };

    return (
    <div className="ss-panel-wrap ss-card ss-fade">
      <div className="ss-phase-header">
        <div className="d-flex justify-content-center">
         <h5 className="ss-phase-title">{phase.title}</h5>
        </div>
        <p className="ss-phase-blurb">{phase.blurb}</p>
      </div>
      <Row className="g-0">
        <Col md={4} lg={3}>
          <div className="ss-tabs" role="tablist" aria-label={phase.title}>
            {services.map((s, i) => (
              <button
                key={s.id}
                ref={(el) => (tabRefs.current[i] = el)}
                role="tab"
                id={`tab-${phase.id}-${s.id}`}
                aria-selected={active === s.id}
                aria-controls={`panel-${phase.id}-${s.id}`}
                tabIndex={active === s.id ? 0 : -1}
                className={`ss-tab ${active === s.id ? "active" : ""}`}
                onClick={() => setActive(s.id)}
                onKeyDown={(e) => handleKeyDown(e, i)}
              >
                <h5 className="ss-tab-num">{String(i + 1).padStart(2, "0")}</h5>
                <h5>{s.title}</h5>
              </button>
            ))}
          </div>
        </Col>
        <Col md={8} lg={9}>
          <div
            key={current.id}
            className="ss-content ss-fade"
            role="tabpanel"
            id={`panel-${phase.id}-${current.id}`}
            aria-labelledby={`tab-${phase.id}-${current.id}`}
          >
            <p>{current.description}</p>
            <ul className="ss-involves">
              {current.involves.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="ss-fit">
              <p>
                <strong>Great for:</strong> {current.fit}
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default function Studios() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="ss-page" style={{ opacity: show ? 1 : 0, transition: "opacity 0.6s ease" }}>
      <style>{styles}</style>

      <Container>
        {/* Hero */}
        <section className="ss-hero ss-fade">
          <div className="d-flex justify-content-center">
            <h1>Studio Services</h1>
          </div>
           <p>
            Concept to premiere, we've got your back. Pitch with AI-powered visuals, run
            your shoot on tools built for production, and deliver to clients and audiences
            on a platform you own. One team, every stage, built by people who make films
            too.
            </p>
        </section>

        
        {/* Phases */}
              {PHASES.map((phase) => (
          <div key={phase.id} className="ss-phase">
            <PhaseCard phase={phase} />
          </div>
        ))}

        {/* Process */}
        <h5 className="ss-section-title">How We Work</h5>
        <Row className="g-3">
          {STEPS.map((step, i) => (
            <Col key={step.title} xs={12} sm={6} lg={3}>
              <div className="ss-step ss-card">
                <div className="ss-step-header">
                  <h5 className="ss-step-num">{`0${i + 1}`}</h5>
                  <h5>{step.title}</h5>
                </div>
                <p>{step.text}</p>
              </div>
            </Col>
          ))}
        </Row>

        {/* CTA */}
        <section className="ss-cta ss-card">
          <div className="d-flex justify-content-center">
            <h5>Let's Build Something for Your Studio</h5>
          </div>
          <p>
            Tell us how your team works today and where it hurts. We'll show you what a
            custom platform could look like.
          </p>
          <div className="d-flex justify-content-center">
  <Button
    href={CONTACT_HREF}
    className="process-card-text hover-brighten"
    style={{
      fontSize: '2.5vmin',
      backgroundColor: 'rgb(67, 66, 66)',
      border: '2px solid rgba(28, 26, 26, 0.9)',
      boxShadow: `
        0 2px 0 rgba(255, 255, 255, 0.12) inset,
        0 -1px 0 rgba(0, 0, 0, 0.45) inset,
        0 6px 12px rgba(0, 0, 0, 0.5),
        0 12px 24px rgba(0, 0, 0, 0.35)
      `,
      borderRadius: '999px',
      cursor: 'pointer',
      padding: '.5rem .1rem',
    }}
  >
    <h5 style={{ margin: '1%' }}>Start the Conversation</h5>
  </Button>
</div>
        </section>
      </Container>
    </div>
  );
}